import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import {
    AppBar, Toolbar, IconButton, Drawer, List, ListItem,
    ListItemButton, ListItemText, Box, Typography, Switch,
    Divider, styled, useTheme,Button
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloudIcon from "@mui/icons-material/Cloud"; // Swapped for Sky Theme
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import crystalCareerImg from "../../assets/crystalcareerimg.png";

// --- Styled Components ---

const TopInfoBar = styled("div")(({ theme }) => ({
    backgroundColor: "#09122C",
    color: "#BBE0EF", // Accent color for the info bar
    fontWeight: 700,
    width: "100%",
    fontSize: "0.7rem",
    padding: "12px 0",
    textAlign: "center",
    letterSpacing: "2px",
    textTransform: "uppercase",
}));

const StyledAppBar = styled(AppBar)(({ theme, skyMode }) => ({
    // If skyMode is on, Header stays Navy to contrast with the blue background
    backgroundColor: skyMode ? "#09122C" : "#FFFFFF",
    color: skyMode ? "#FFFFFF" : "#09122C",
    boxShadow: skyMode ? "0 4px 20px rgba(0,0,0,0.2)" : "0 2px 10px rgba(0,0,0,0.05)",
    borderBottom: skyMode ? "none" : "1px solid #eee",
    transition: "all 0.4s ease-in-out",
}));

const FlexToolbar = styled(Toolbar)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: 90,
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "0 40px",
    [theme.breakpoints.down("sm")]: {
        padding: "0 20px",
        height: 75,
    },
}));

const NavLinkText = styled(Typography)(({ active, skymode }) => ({
    fontSize: "0.85rem",
    fontWeight: 800,
    position: "relative",
    cursor: "pointer",
    // In SkyMode, inactive text is white; otherwise it is Navy
    color: active ? "#B8860B" : (skymode ? "#FFFFFF" : "#09122C"),
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    transition: "color 0.3s ease",
    "&:after": {
        content: '""',
        position: "absolute",
        width: active ? "100%" : "0%",
        height: "3px",
        bottom: "-6px",
        left: "0",
        backgroundColor: "#B8860B",
        transition: "width 0.3s ease",
    },
    "&:hover:after": {
        width: "100%",
    },
}));

const LogoImg = styled("img")(({ theme }) => ({
    height: 65,
    width: "auto",
    filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.1))",
    [theme.breakpoints.down("sm")]: {
        height: 55,
    },
}));

// --- Main Component ---

export default function Header({ user, darkMode, setDarkMode }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const auth = getAuth();
    const theme = useTheme();

    // Context check: Is the global theme currently set to Sky Blue?
    const isSkyActive = darkMode;

    const toggleDrawer = () => setOpen(!open);
    const handleSignOut = () => {
        signOut(auth).then(() => (window.location.href = "/")).catch(console.error);
    };

    const navLinks = [
        { label: "Home", to: "/home" },
        { label: "Founder", to: "/about" },
        { label: "Courses", to: "/educational-courses" },
        { label: "Languages", to: "/language-courses" },
    ];

    const userLinks = user
        ? [
            { label: "Dashboard", to: "/dashboard" },
            { label: "Profile", to: "/profile" },
        ]
        : [{ label: "Trainee Portal", to: "/login" }];

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <TopInfoBar>
                Empowering Job Seekers • Global Admissions • Career Focused Training
            </TopInfoBar>

            <StyledAppBar position="sticky" skyMode={isSkyActive}>
                <FlexToolbar>

                    {/* ✅ LOGO */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Link to="/home" style={{ display: "flex", alignItems: "center" }}>
                            <LogoImg src={crystalCareerImg} alt="Crystal Career Logo" />
                        </Link>
                    </Box>

                    {/* ✅ DESKTOP NAV */}
                    <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 4 }}>
                        {[...navLinks, ...userLinks].map((item) => (
                            <Link key={item.to} to={item.to} style={{ textDecoration: "none" }}>
                                <NavLinkText active={isActive(item.to)} skymode={isSkyActive ? 1 : 0}>
                                    {item.label}
                                </NavLinkText>
                            </Link>
                        ))}

                        {user && (
                            <IconButton onClick={handleSignOut} sx={{ color: isSkyActive ? "#BBE0EF" : "inherit", ml: 1 }}>
                                <LogoutIcon fontSize="small" />
                            </IconButton>
                        )}

                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            ml: 2,
                            bgcolor: isSkyActive ? "rgba(187, 224, 239, 0.1)" : "rgba(0,0,0,0.05)",
                            p: "4px 12px",
                            borderRadius: "20px"
                        }}>
                            {isSkyActive ? <CloudIcon sx={{ fontSize: 18, color: "#BBE0EF" }} /> : <WbSunnyIcon sx={{ fontSize: 18, color: "#B8860B" }} />}
                            <Switch
                                size="small"
                                checked={darkMode}
                                onChange={() => setDarkMode(!darkMode)}
                                color="secondary"
                            />
                        </Box>
                    </Box>

                    {/* ✅ MOBILE TRIGGER */}
                    <IconButton
                        onClick={toggleDrawer}
                        sx={{ display: { xs: "flex", md: "none" }, color: "inherit" }}
                    >
                        <MenuIcon fontSize="large" />
                    </IconButton>

                </FlexToolbar>
            </StyledAppBar>

            {/* Mobile Drawer (Refined for Sky Mode) */}
            <Drawer anchor="right" open={open} onClose={toggleDrawer}>
                <Box sx={{
                    width: 280,
                    height: "100%",
                    bgcolor: isSkyActive ? "#BBE0EF" : "#FFFFFF",
                    color: "#09122C",
                    p: 4
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 1, color: "#09122C" }}>MENU</Typography>
                    <Typography variant="caption" sx={{ color: "#B8860B", fontWeight: 700, letterSpacing: 1 }}>CRYSTAL CAREER</Typography>

                    <Divider sx={{ my: 3, borderColor: "rgba(0,0,0,0.1)" }} />

                    <List>
                        {[...navLinks, ...userLinks].map((item) => (
                            <ListItem key={item.to} disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    component={Link}
                                    to={item.to}
                                    onClick={toggleDrawer}
                                    sx={{
                                        borderRadius: "12px",
                                        bgcolor: isActive(item.to) ? "rgba(9, 18, 44, 0.05)" : "transparent"
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: isActive(item.to) ? 900 : 600,
                                            fontSize: "1rem",
                                            color: "#09122C"
                                        }}
                                    />
                                    <ArrowForwardIosIcon sx={{ fontSize: 12, color: "#B8860B" }} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                    {user && (
                        <Box sx={{ mt: "auto", pt: 4 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<LogoutIcon />}
                                onClick={handleSignOut}
                                sx={{ color: "#09122C", borderColor: "#09122C", fontWeight: 700 }}
                            >
                                Sign Out
                            </Button>
                        </Box>
                    )}
                </Box>
            </Drawer>
        </>
    );
}
